package br.gov.go.pge.permanenciaweb.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import br.gov.go.pge.permanenciaweb.site.pages.DashboardState
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Small
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.KeyboardEvent

val PhotoDialogStyle = CssStyle.base {
    Modifier
        .backgroundColor(IndustrialPalette.White)
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.375.cssRem)
        .padding(1.25.cssRem)
        .width(100.percent)
        .maxWidth(48.cssRem)
        .maxHeight(88.percent)
        .overflow(Overflow.Auto)
        .styleModifier { property("position", "relative") }
}

// The real per-entry capture is read from AcessoAnaliticoClass.read/log_extra. Keep this switch
// for a quick rollback if WE stops exposing the report or the capture window needs adjustment.
private const val SHOW_FACIAL_CAPTURE = true

val PhotoDialogRowStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .styleModifier {
            // Fixed row: `weight` alone grows from the content's intrinsic width, which made the
            // two photo frames visibly different sizes. The cards stay compact so the actions
            // remain above the modal's lower edge.
            property("display", "grid")
            property(
                "grid-template-columns",
                if (SHOW_FACIAL_CAPTURE) "repeat(2, minmax(0, 1fr))" else "minmax(0, 1fr)",
            )
            property("gap", "1rem")
            // Keep the card compact so the photo does not push the actions below the modal.
            property("justify-items", "center")
        }
}

val PhotoCardStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .styleModifier { property("flex-direction", "column") }
        .gap(0.5.cssRem)
        .padding(0.75.cssRem)
        .width(100.percent)
        .maxWidth(24.cssRem)
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.25.cssRem)
        .backgroundColor(IndustrialPalette.Surface)
}

val PhotoFrameStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .styleModifier {
            property("position", "relative")
            property("aspect-ratio", "1 / 1")
            property("overflow", "hidden")
            property("border-radius", "0.25rem")
            property("background", "#EEF2F4")
        }
        .display(DisplayStyle.Flex)
        .justifyContent(JustifyContent.Center)
}

val PhotoPlaceholderStyle = CssStyle.base {
    Modifier
        .color(IndustrialPalette.Muted)
        .textAlign(TextAlign.Center)
        .styleModifier {
            property("font-size", "2rem")
            property("font-weight", "600")
            property("letter-spacing", "0.08em")
            property("align-self", "center")
        }
}

val PhotoDialogActionsStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .justifyContent(JustifyContent.FlexEnd)
        .gap(0.5.cssRem)
        .margin(top = 1.cssRem)
}

val PhotoCardTitleStyle = CssStyle.base {
    Modifier
        .margin(0.cssRem)
        .styleModifier { property("font-size", "0.9375rem") }
}

/** Thin indeterminate ruler pinned to the modal's lower edge while either image is loading. */
val PhotoLoadingBarStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .height(2.px)
        .backgroundColor(IndustrialPalette.Border)
        .styleModifier {
            property("position", "absolute")
            property("left", "0")
            property("right", "0")
            property("bottom", "0")
            property("overflow", "hidden")
            property("border-radius", "0 0 0.375rem 0.375rem")
        }
}

val PhotoLoadingSegmentStyle = CssStyle.base {
    Modifier
        .width(35.percent)
        .height(2.px)
        .backgroundColor(IndustrialPalette.Steel)
        .styleModifier {
            property("position", "absolute")
            property("left", "0")
            property("top", "0")
            property("animation", "photo-loading-ruler 1.1s linear infinite")
        }
}

private enum class PhotoLoadState {
    LOADING,
    LOADED,
    MISSING,
}

@Composable
internal fun PhotoDialog(state: DashboardState) {
    val person = state.pendingPhotos ?: return

    var cadastroState by remember(person.idpessoa) { mutableStateOf(PhotoLoadState.LOADING) }
    var facialState by remember(person.idpessoa) {
        mutableStateOf(if (SHOW_FACIAL_CAPTURE) PhotoLoadState.LOADING else PhotoLoadState.MISSING)
    }
    val loading = cadastroState == PhotoLoadState.LOADING ||
        (SHOW_FACIAL_CAPTURE && facialState == PhotoLoadState.LOADING)

    DisposableEffect(person.idpessoa) {
        val listener = EventListener { event ->
            if ((event as? KeyboardEvent)?.key == "Escape") state.closePhotos()
        }
        window.addEventListener("keydown", listener)
        onDispose { window.removeEventListener("keydown", listener) }
    }

    Overlay(contentAlignment = Alignment.Center) {
        Box(Modifier.fillMaxSize().onClick { state.closePhotos() })

        Column(modifier = PhotoDialogStyle.toModifier().then(Modifier.id("photo-dialog"))) {
            H3(attrs = PhotoCardTitleStyle.toAttrs()) {
                Text(if (SHOW_FACIAL_CAPTURE) "Fotos — ${person.nome}" else "Foto — ${person.nome}")
            }
            P {
                Text(
                    if (SHOW_FACIAL_CAPTURE) {
                        "Foto de cadastro e registro mais recente do reconhecimento facial."
                    } else {
                        "Foto de cadastro da pessoa no sistema de acesso."
                    }
                )
            }

            Div(attrs = PhotoDialogRowStyle.toAttrs()) {
                PhotoCard(
                    title = "Foto de cadastro",
                    url = "/api/roster/${person.idpessoa}/photo/cadastro",
                    name = person.nome,
                    note = "Imagem do cadastro da pessoa no sistema de acesso.",
                    state = cadastroState,
                    onLoaded = { cadastroState = PhotoLoadState.LOADED },
                    onMissing = { cadastroState = PhotoLoadState.MISSING },
                )
                if (SHOW_FACIAL_CAPTURE) {
                    PhotoCard(
                        title = "Última captura facial",
                        url = "/api/roster/${person.idpessoa}/photo/facial",
                        name = person.nome,
                        note = "Registro facial mais recente vinculado ao acesso de hoje.",
                        state = facialState,
                        onLoaded = { facialState = PhotoLoadState.LOADED },
                        onMissing = { facialState = PhotoLoadState.MISSING },
                    )
                }
            }

            Div(attrs = PhotoDialogActionsStyle.toAttrs()) {
                Button(
                    onClick = { state.closePhotos() },
                    variant = DefaultButtonVariant,
                    size = ButtonSize.SM,
                ) {
                    Text("Fechar")
                }
            }

            if (loading) {
                Div(
                    attrs = PhotoLoadingBarStyle.toAttrs {
                        attr("role", "progressbar")
                        attr("aria-label", "Carregando fotos")
                    },
                ) {
                    Div(attrs = PhotoLoadingSegmentStyle.toAttrs())
                }
            }
        }
    }
}

@Composable
private fun PhotoCard(
    title: String,
    url: String,
    name: String,
    note: String,
    state: PhotoLoadState,
    onLoaded: () -> Unit,
    onMissing: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Div(attrs = PhotoCardStyle.toModifier().then(modifier).toAttrs()) {
        H3(attrs = PhotoCardTitleStyle.toAttrs()) { Text(title) }
        Div(attrs = PhotoFrameStyle.toAttrs()) {
            // The placeholder stays behind the image: while loading it is the "standard image"
            // visible to the user, and on 404/502 it remains instead of a broken-image icon.
            Div(attrs = PhotoPlaceholderStyle.toAttrs()) { Text(initials(name)) }
            Img(
                src = url,
                alt = "$title de $name",
                attrs = {
                    style {
                        property("position", "absolute")
                        property("inset", "0")
                        property("width", "100%")
                        property("height", "100%")
                        property("object-fit", "cover")
                        property("display", if (state == PhotoLoadState.MISSING) "none" else "block")
                    }
                    ref { image ->
                        val loadedListener = EventListener { onLoaded() }
                        val errorListener = EventListener { onMissing() }
                        image.addEventListener("load", loadedListener)
                        image.addEventListener("error", errorListener)
                        // The image may already be complete when the ref runs (browser cache).
                        if (image.complete) {
                            if (image.naturalWidth > 0) onLoaded() else onMissing()
                        }
                        onDispose {
                            image.removeEventListener("load", loadedListener)
                            image.removeEventListener("error", errorListener)
                        }
                    }
                },
            )
        }
        Small { Text(if (state == PhotoLoadState.MISSING) "Foto não disponível." else note) }
    }
}

private fun initials(name: String): String {
    val parts = name.trim().split(Regex("\\s+")).filter { it.isNotEmpty() }
    return parts.take(2).mapNotNull { it.firstOrNull()?.uppercaseChar() }.joinToString("")
        .ifEmpty { "?" }
}
